import {
  Box,
  Paper,
  Typography,
} from '@mui/material'
import type * as React from 'react'
import { useContext, useEffect, useState } from 'react'
import { LanguageContext } from '../util/LanguageContext'
import { detectTextDirection } from '../util/TextDirection'

type AnonymousConversationSummaryProps = {
  date: string | undefined
  text: string | undefined
  text2?: string
  text_lang?: string
  text2_lang?: string
  id?: string
}

export const AnonymousConversationSummary: React.FC<AnonymousConversationSummaryProps> = ({
  date,
  text,
  text2,
  text_lang,
  text2_lang,
  id,
}) => {
  // スマホ用vh
  const [vh, setVh] = useState(window.innerHeight * 0.01)
  // 文字列変換用
  const translation = useContext(LanguageContext)

  // 要約表示順:
  // 1つ目: text  = 右側言語 / 来客者言語の要約
  // 2つ目: text2 = 左側言語 / 窓口言語の要約
  // PDFと同じ順番で表示する。
  const baseBlocks = [
    text ? { title: text_lang || '', body: text } : null,
    text2 ? { title: text2_lang || '', body: text2 } : null,
  ].filter((b): b is { title: string; body: string } => b !== null)

  const sameLang =
    text_lang &&
    text2_lang &&
    text_lang === text2_lang

  // 同一言語の場合は同じ内容が重複するため、1つ目の要約枠のみ表示する。
  const blocks = sameLang
    ? baseBlocks.slice(0, 1)
    : baseBlocks

  useEffect(() => {
    const handleResize = () => setVh(window.innerHeight * 0.01)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      {/* ヘッダー（日付 + Conversation record ID） */}
      <Box sx={{ textAlign: 'center', borderBottom: '2px solid #282828', pb: 1, mb: 2 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{date}</Typography>
        {id ? (
          <Box sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
              {translation['label_conversation_ID'] || 'Conversation record ID:'}
            </Typography>
            <Typography sx={{ fontSize: 14 }}>{id}</Typography>
          </Box>
        ) : null}
      </Box>

      {/* 言語ブロック */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pb: 2 }}>
        {blocks.map((b, i) => (
          <Box key={i}>
            {b.title && (
              <Typography sx={{ fontSize: 14, fontWeight: 700, mb: 1 }}>
                {b.title}
              </Typography>
            )}

            {/* 灰色スクロール枠 */}
            <Paper
              elevation={0}
              sx={{
              backgroundColor: '#EFEFEF',
              borderRadius: 1,
              p: 1.5,
              maxHeight: 140, // 枠内スクロールにする高さ
              overflowY: 'auto',
              }}
            >
              <div
                style={{
                font: 'normal normal normal 12px Noto Sans JP',
                direction: detectTextDirection(b.body || ''),
                }}
                dangerouslySetInnerHTML={{
                __html: b.body ? b.body.replace(/\n/g, '<br />') : '',
                }}
              />
            </Paper>
          </Box>
        ))}
      </Box>
    </Box>
  )
}
